import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Grid, Typography, Progress, Spin, Modal } from 'antd';
import { ExportOutlined, RightOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetUniversalScoreQuery } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PassionITScoreDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const { data: universalScoreRes, isLoading: isUniversalScoreLoading } = useGetUniversalScoreQuery(token, { skip: !token });

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Modal State
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedHeir, setSelectedHeir] = useState<any>(null);

    const showModal = (heir: any) => {
        setSelectedHeir(heir);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedHeir(null);
    };

    const getRecommendation = (score: number) => {
        if (score === undefined || score === null) return "N/A";
        if (score >= 4.5) return "Excellent - Continue current path";
        if (score >= 3.5) return "Good - Minor improvements needed";
        if (score >= 2.5) return "Average - Significant improvements needed";
        return "Critical - Immediate action required";
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // Real Data for PassionIT Scores
    const heirScores = universalScoreRes?.data || universalScoreRes || [];

    const renderScoreBadge = (score: number) => {
        let color = '#52c41a'; // Green for high scores
        if (score < 4) color = '#1890ff'; // Blue
        if (score < 3) color = '#faad14'; // Yellow/Orange
        if (score < 2) color = '#f5222d'; // Red

        return (
            <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px',
                margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        PASSIONIT — Reliance Family Office
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">PassionIT Score</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - PassionIT Scores */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <Title level={4} className="mb-4">PASSIONIT Universal Score</Title>

                        <Text type="secondary" className="mb-3 d-block">Showing {heirScores?.length || 0} heirs</Text>

                        {/* Score Cards for Each Heir */}
                        <Spin spinning={isUniversalScoreLoading}>
                            {heirScores.map((heir: any, index: number) => (
                                <Card
                                    key={heir.entityId || index}
                                    className="mb-3"
                                    bordered={false}
                                    style={{ backgroundColor: '#fafafa' }}
                                >
                                    <Row gutter={16} align="middle">
                                        <Col span={4}>
                                            <div>
                                                <Text strong style={{ display: 'block', fontSize: '16px' }}>{heir.name}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{heir.entityType || 'Heir'}</Text>
                                            </div>
                                        </Col>
                                        <Col span={3}>
                                            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                                                <Text type="secondary" style={{ fontSize: '10px' }}>Governance</Text>
                                            </div>
                                            {renderScoreBadge(heir.governanceScore || 0)}
                                        </Col>
                                        <Col span={3}>
                                            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                                                <Text type="secondary" style={{ fontSize: '10px' }}>Succession</Text>
                                            </div>
                                            {renderScoreBadge(heir.successionScore || 0)}
                                        </Col>
                                        <Col span={3}>
                                            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                                                <Text type="secondary" style={{ fontSize: '10px' }}>Mentorship</Text>
                                            </div>
                                            {renderScoreBadge(heir.mentorshipScore || 0)}
                                        </Col>
                                        <Col span={3}>
                                            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                                                <Text type="secondary" style={{ fontSize: '10px' }}>PRUTL</Text>
                                            </div>
                                            {renderScoreBadge(heir.prutlScore || 0)}
                                        </Col>
                                        <Col span={4}>
                                            <div style={{ textAlign: 'center' }}>
                                                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>{heir.overallScore}</Text>
                                                <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Overall Score</Text>
                                            </div>
                                        </Col>
                                        <Col span={4}>
                                            <a href="#" style={{ fontSize: '12px' }} onClick={(e) => { e.preventDefault(); showModal(heir); }}>View Detailed Analysis →</a>
                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                        </Spin>
                    </Card>
                </Col>
            </Row>

            {/* Detailed Analysis Modal */}
            <Modal
                title={`${selectedHeir?.name || 'Heir'} - PASSIONIT Score Details`}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose}>
                        Close
                    </Button>,
                    <Button key="action" type="primary" onClick={() => console.log('Take action clicked')}>
                        Take Action
                    </Button>,
                ]}
            >
                {selectedHeir && (
                    <div>
                        <p style={{ marginTop: 0 }}><strong>Overall Score:</strong> {selectedHeir.overallScore}</p>

                        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                            <p style={{ margin: 0, fontWeight: 500, marginBottom: '8px' }}>Breakdown:</p>
                            <ul style={{ paddingLeft: '20px', margin: 0, listStyleType: 'disc' }}>
                                <li>Technical/Governance: {selectedHeir.governanceScore || 0}/5</li>
                                <li>People/Succession: {selectedHeir.successionScore || 0}/5</li>
                                <li>Mentorship: {selectedHeir.mentorshipScore || 0}/5</li>
                                <li>Values/PRUTL: {selectedHeir.prutlScore || 0}/5</li>
                            </ul>
                        </div>

                        <p style={{ marginBottom: 0 }}><strong>Recommendation:</strong> {getRecommendation(selectedHeir.overallScore)}</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PassionITScoreDashboard;
